import Vec2 from 'vec2';

import {
    ARROW_LENGTH_GRANULARITY_IN_INCHES,
    A_SMIDGEN,
    BACKSPLASH_INPUT_LIMIT,
    CONSTRAINT_REFINEMENT_START_MARGIN,
    DEFAULT_CORNER_RADIUS,
    EDGE_LENGTH_INPUT_LIMIT,
    MAGIC_NUMBER_2,
    MINIMUM_ANGLE_TO_ENFORCE_GAP,
    MINIMUM_GAP,
    WATERFALL_INPUT_LIMIT,
} from '@common/constants';
import { SideItemOption, containsBumpOut, containsRadiusOut } from '@common/enums';
import {
    MathFunctions,
    roundToArrowLengthGranularity,
    roundToCornerRadiusGranularity,
    roundToNearest,
} from '@common/helpers';
import { ArrowInfo, CornerInfo, DrawingEdge, DrawingShape } from '@common/models';
import { adjustValueForMinMax } from '@common/utils';

import drawingTool from '../../../drawer/drawingTool';
import { findArrowByEdgeId, findDrawingEdgeById } from './drawing-shape-helper';
import { adjustEdgeForDiagonals, updateShapeByEdge, updateShapeByRotation } from './edit-dimension-helper';

/**
 * Constraints are _very_ inefficent, this flag lets us turn them off almost completely.
 */
const SKIP_CONSTRAINTS = true;

type SimplePoint = { x: number; y: number };

//Goes through the entire shape and calculates the minimum feasible edge lengths
// eslint-disable-next-line sonarjs/cognitive-complexity
export const calculateEdgeConstraints = (shape: DrawingShape): void => {
    if (!shape.arrowsInfo) {
        return;
    }
    // clearing any old constraints
    shape.arrowsInfo.forEach((arrow) => {
        arrow.maximumLength = undefined;
        arrow.minimumLength = undefined;
    });

    for (let i = 0; i < shape.arrowsInfo.length; i++) {
        const curArrow = shape.arrowsInfo[i];
        const curEdge = findDrawingEdgeById(curArrow.drawingEdgeId!, shape);

        if (!curEdge || !curArrow || !curArrow.size) {
            continue;
        }

        const skIndex = getSkeletonIndex(shape, curEdge);
        const parallelEdge = findParallelEdge(shape, curEdge);
        if (!parallelEdge) {
            continue;
        }

        const parallelArrow = findArrowByEdgeId(parallelEdge.id, shape);
        if (!parallelArrow || !parallelArrow.size) {
            continue;
        }

        const dif = roundToNearest(curArrow.size - parallelArrow.size, ARROW_LENGTH_GRANULARITY_IN_INCHES);

        let enforceMinGap = false;
        //if edge is not beginning or end of skeleton
        if (skIndex !== 0 && skIndex < shape.skeleton.length - 2) {
            // if the angle of the preceeding side to the succeeding side is less than
            // MINIMUM_ANGLE_TO_ENFORCE_GAP, enforce the gap.
            const preceedingSide = MathFunctions.subtractVectors(
                shape.skeleton[skIndex].pos,
                shape.skeleton[skIndex - 1].pos
            );
            const succeedingSide = MathFunctions.subtractVectors(
                shape.skeleton[skIndex + 1].pos,
                shape.skeleton[skIndex + 2].pos
            );

            if (MathFunctions.angleBetween(preceedingSide, succeedingSide) < MINIMUM_ANGLE_TO_ENFORCE_GAP) {
                enforceMinGap = true;
            }
        }

        if (enforceMinGap) {
            curArrow.minimumLength = dif + MINIMUM_GAP;
            parallelArrow.minimumLength = MINIMUM_GAP - adjustEdgeForDiagonals(shape, parallelEdge);
        } else {
            curArrow.minimumLength = dif + EDGE_LENGTH_INPUT_LIMIT.MIN;
        }

        curArrow.maximumLength = calculateEdgeMaximums(shape, curEdge);
    }

    // Only refine a shape's edge constraints if it isn't already intersecting with itself
    // as can happen currently when a shape is free-drawn.  This allows the user to correct.
    if (!MathFunctions.polygonCollidesWithItself(shape.highlightPoints || [])) {
        refineEdgeConstraintsUsingWhatIfs(shape);
    }
};

const getInOrOutCornerSequence = (shape: DrawingShape): string => {
    return Object.entries(shape.cornerMap).reduce((acc, entry) => {
        const cornerAngle = entry[1].angle;
        if (cornerAngle) {
            if (cornerAngle < 0) {
                return acc + 'I';
            }
            return acc + 'O';
        }
        return acc;
    }, '');
};

const shapeHasDiagonalsWithFlippedAdjacents = (shape: DrawingShape): boolean => {
    return Object.values(shape.cornerMap).some((cornerInfo) => isDiagonalWithFlippedAdjacents(shape, cornerInfo));
};

const shapeHasViolatedMinGap = (shape: DrawingShape): boolean => {
    return Object.values(shape.drawingEdges).some((edge) => violatesMinGap(shape, edge));
};

const isDiagonalWithFlippedAdjacents = (shape: DrawingShape, cornerInfo: CornerInfo): boolean => {
    if (cornerInfo.isVisibleInCanvas && cornerInfo.diagonalLength && cornerInfo.angle === -90) {
        const farEndofAdjacentSideMinimumDistance =
            MathFunctions.getDistanceBetween(cornerInfo.coords, cornerInfo.replacementStartCoords!) + 1;

        const leftEdge = shape.drawingEdges.filter((e) => e.startCornerId === cornerInfo.id)[0];
        const rightEdge = shape.drawingEdges.filter((e) => e.endCornerId === cornerInfo.id)[0];

        if (leftEdge && rightEdge) {
            const distToFarLeftAdjEnd = MathFunctions.getDistanceBetween(cornerInfo.coords, leftEdge.endDisplayVec);
            const distToFarRightAdjEnd = MathFunctions.getDistanceBetween(cornerInfo.coords, rightEdge.startDisplayVec);

            return (
                distToFarLeftAdjEnd < farEndofAdjacentSideMinimumDistance ||
                distToFarRightAdjEnd < farEndofAdjacentSideMinimumDistance
            );
        }
    }

    return false;
};

const shapeIsInvalid = (clonedShape: DrawingShape, origArrow: ArrowInfo, whatIfArrow: ArrowInfo): boolean => {
    if (SKIP_CONSTRAINTS || !clonedShape.arrowsInfo?.length) {
        return false;
    }
    const originalInOrOutCornerSequence = getInOrOutCornerSequence(clonedShape);

    // update cloned shape with whatif sized arrow
    updateShapeByEdge(whatIfArrow, clonedShape, true);
    drawingTool.edgeAndCornerCalcs(clonedShape);
    drawingTool.addArrowInfoData(clonedShape);

    const newInOrOutCornerSequence = getInOrOutCornerSequence(clonedShape);

    let invalidShape = false; // innocent until proven guilty

    // 1. check for corners that flipped from in to out or from out to in
    if (newInOrOutCornerSequence !== originalInOrOutCornerSequence) {
        invalidShape = true;
    }

    // 2. check for inside diagonals with adjacent sides that flipped direction
    if (shapeHasDiagonalsWithFlippedAdjacents(clonedShape)) {
        invalidShape = true;
    }

    // 3. check for minimum gap violations on G and similar shapes
    if (shapeHasViolatedMinGap(clonedShape)) {
        invalidShape = true;
    }

    if (!invalidShape) {
        const newPoly = clonedShape.highlightPoints || [];
        // 4. check for self intersections
        invalidShape = MathFunctions.polygonCollidesWithItself(newPoly);
        if (!invalidShape) {
            // 5. check for sides that would be absolutely too small or too large
            invalidShape = clonedShape.arrowsInfo.some((arrowInfo) => {
                return (
                    arrowInfo.size &&
                    (arrowInfo.size < EDGE_LENGTH_INPUT_LIMIT.MIN || arrowInfo.size > EDGE_LENGTH_INPUT_LIMIT.MAX)
                );
            });
        }
    }

    // return the cloned shape to pre-whatif state for next check
    updateShapeByEdge(origArrow, clonedShape, true);
    drawingTool.edgeAndCornerCalcs(clonedShape);
    drawingTool.addArrowInfoData(clonedShape);

    return invalidShape;
};

const getLargestAcceptableArrowLength = (
    clonedShape: DrawingShape,
    arrowIndex: number,
    whatIfSize: number,
    largestGood: number,
    smallestBad: number
): number => {
    if (SKIP_CONSTRAINTS || !clonedShape.arrowsInfo) {
        return EDGE_LENGTH_INPUT_LIMIT.MAX;
    }
    const origArrow = DrawingShape.cloneArrowInfo(clonedShape.arrowsInfo[arrowIndex]);
    const whatIfArrow = DrawingShape.cloneArrowInfo(clonedShape.arrowsInfo[arrowIndex]);
    whatIfArrow.size = whatIfSize;
    let newWhatIfSize;
    let newLargestGood = largestGood;
    let newSmallestBad = smallestBad;

    const invalidShape = shapeIsInvalid(clonedShape, origArrow, whatIfArrow);

    if (invalidShape) {
        // create next what if size as the midpoint between the current whatif and the largest good size yet
        newWhatIfSize = roundToArrowLengthGranularity((whatIfSize - largestGood) / 2 + largestGood);
        if (newWhatIfSize === largestGood || newWhatIfSize === whatIfSize) {
            return largestGood;
        }
        if (whatIfSize < smallestBad) {
            newSmallestBad = whatIfSize;
        }
    } else {
        // create next what if size as the midpoint between the current whatif and the smallest bad size yet
        newWhatIfSize = roundToArrowLengthGranularity((smallestBad - whatIfSize) / 2 + whatIfSize);
        if (newWhatIfSize === smallestBad || newWhatIfSize === whatIfSize) {
            return whatIfSize;
        }
        if (whatIfSize > largestGood) {
            newLargestGood = whatIfSize;
        }
    }

    // recursive call to get to the largest good arrow length (one that doesn't create the above 1/2/3 issues for shape)
    return getLargestAcceptableArrowLength(clonedShape, arrowIndex, newWhatIfSize, newLargestGood, newSmallestBad);
};

const getSmallestAcceptableArrowLength = (
    clonedShape: DrawingShape,
    arrowIndex: number,
    whatIfSize: number,
    smallestGood: number,
    largestBad: number
): number => {
    if (SKIP_CONSTRAINTS || !clonedShape.arrowsInfo) {
        return EDGE_LENGTH_INPUT_LIMIT.MIN;
    }
    const origArrow = DrawingShape.cloneArrowInfo(clonedShape.arrowsInfo[arrowIndex]);
    const whatIfArrow = DrawingShape.cloneArrowInfo(clonedShape.arrowsInfo[arrowIndex]);
    whatIfArrow.size = whatIfSize;
    let newWhatIfSize;
    let newSmallestGood = smallestGood;
    let newLargestBad = largestBad;

    const invalidShape = shapeIsInvalid(clonedShape, origArrow, whatIfArrow);

    /**
     * Granularity of the 'new what if size'.
     * It should be lower than {@link ARROW_LENGTH_GRANULARITY_IN_INCHES}
     * Too small and too many unnecessary tests will run making this function inefficient
     * Too large and the test will skip at least one lower valid value @see {@link https://inscyth-inc.atlassian.net/jira/software/c/projects/EZE/boards/102?modal=detail&selectedIssue=EZE-858&search=858}
     */

    if (invalidShape) {
        // create next what if size as the midpoint between the current whatif and the smallest good size yet
        newWhatIfSize = (smallestGood - whatIfSize) / 2 + whatIfSize;
        if (newWhatIfSize === smallestGood || newWhatIfSize === whatIfSize) {
            return roundToArrowLengthGranularity(newWhatIfSize);
        }
        if (whatIfSize > largestBad) {
            newLargestBad = whatIfSize;
        }
    } else {
        // create next what if size as the midpoint between the current whatif and the largest bad size yet
        newWhatIfSize = (whatIfSize - largestBad) / 2 + largestBad;
        if (newWhatIfSize === largestBad || newWhatIfSize === whatIfSize) {
            return roundToArrowLengthGranularity(smallestGood);
        }
        if (whatIfSize < smallestGood) {
            newSmallestGood = whatIfSize;
        }
    }

    // recursive call to get to the smallest good arrow length (one that doesn't create the above 1/2/3 issues for shape)
    return getSmallestAcceptableArrowLength(clonedShape, arrowIndex, newWhatIfSize, newSmallestGood, newLargestBad);
};

const shapeInMemoryRequiresRotation = (shape: DrawingShape): boolean => {
    return (
        shape.skeleton.length > 4 &&
        roundToArrowLengthGranularity(shape.skeleton[0].pos.x) !==
            roundToArrowLengthGranularity(shape.skeleton[1].pos.x) &&
        roundToArrowLengthGranularity(shape.skeleton[0].pos.y) !==
            roundToArrowLengthGranularity(shape.skeleton[1].pos.y)
    );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const refineEdgeConstraintsUsingWhatIfs = (shape: DrawingShape) => {
    if (SKIP_CONSTRAINTS || !shape.arrowsInfo?.length) {
        return;
    }

    for (let i = 0; i < shape.arrowsInfo.length; i++) {
        // TODO : BOB, why does this have to be inside the loop if it never changes?  It hurts!
        // setup a cloned shape in memory to play with. Works cleaner when done for each arrow.
        const clonedShape = DrawingShape.cloneDrawingShape(shape, true, true);

        // if a shape has been rotated, the function that checks the minimum gap, calculateEdgeMaximums won't work
        // so the following conditionally rotates the cloned in memory shape, so it can
        if (shapeInMemoryRequiresRotation(clonedShape)) {
            updateShapeByRotation(undefined, clonedShape, true);
        }

        drawingTool.edgeAndCornerCalcs(clonedShape);
        drawingTool.addArrowInfoData(clonedShape);
        if (!clonedShape.arrowsInfo?.length || !clonedShape.arrowsInfo[i]) {
            return;
        }
        if (!clonedShape.arrowsInfo[i]) {
            continue;
        }
        // save current size and min/max's
        const { size: currArrowSize } = clonedShape.arrowsInfo[i];
        // refine min and max sizes
        if (currArrowSize) {
            // refine maximum constraint using a recursive binary search for the largest arrow size that works
            const refinedMaxLength = getLargestAcceptableArrowLength(
                clonedShape,
                i,
                currArrowSize,
                currArrowSize,
                EDGE_LENGTH_INPUT_LIMIT.MAX + CONSTRAINT_REFINEMENT_START_MARGIN
            );
            shape.arrowsInfo[i].maximumLength = refinedMaxLength;
            // refine minimum constraint using a recursive binary search for the largest arrow size that works
            const refinedMinLength = getSmallestAcceptableArrowLength(
                clonedShape,
                i,
                currArrowSize,
                currArrowSize,
                CONSTRAINT_REFINEMENT_START_MARGIN
            );
            shape.arrowsInfo[i].minimumLength = refinedMinLength;
            // leave console.log commented out for further refinements
            // console.log(
            //     'Arrow ' + shape.arrowsInfo[i].label,
            //     shape.arrowsInfo[i].minimumLength,
            //     shape.arrowsInfo[i].maximumLength
            // );
        }
    }
};

const getSkeletonIndex = (shape: DrawingShape, edge: DrawingEdge) => {
    const startCorner = shape.cornerMap[edge.startCornerId];
    const endCorner = shape.cornerMap[edge.endCornerId];
    if (startCorner.isUpper) {
        return startCorner.skIndex;
    } else {
        return endCorner.skIndex;
    }
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const violatesMinGap = (shape: DrawingShape, edge: DrawingEdge): boolean => {
    if (!shape || !shape.drawingEdges) {
        return false;
    }
    const arrow = findArrowByEdgeId(edge.id, shape);
    if (arrow?.size) {
        const max = calculateEdgeMaximums(shape, edge);
        if (max && max + A_SMIDGEN < arrow.size) {
            return true;
        }
    }
    return false;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const calculateEdgeMaximums = (shape: DrawingShape, edge: DrawingEdge) => {
    if (!shape || !shape.drawingEdges) {
        return;
    }
    const edgeSkIndex = getSkeletonIndex(shape, edge);
    const startCord = shape.cornerMap[edge.startCornerId].coords;
    const endCord = shape.cornerMap[edge.endCornerId].coords;

    // Determining if the edge is at the end of a skeleton. If the edge is not part of the last skeleton length, there are no maximums to apply
    if (edgeSkIndex !== 0 && edgeSkIndex !== shape.skeleton.length - 2) {
        return;
    }

    const beginningOfSkel: boolean = edgeSkIndex === 0;

    let clearance;
    for (let i = 0; i < shape.drawingEdges.length; i++) {
        //Excludes edges that are not at least 3 skeleton lengths away
        const testEdge = shape.drawingEdges[i];
        if (Math.abs(getSkeletonIndex(shape, shape.drawingEdges[i]) - edgeSkIndex) < 3) {
            continue;
        }

        //Builds a rectangular keepout zone for the test edge represented by the top right (tr) and bottom left (bl) corners
        const keepOutRect = buildKeepOutRectangle(testEdge, shape);

        // Removing massive if statement for vertical and horizontal edges with these functions
        let dirXy = (obj: Vec2 | SimplePoint) => roundToArrowLengthGranularity(obj.y);
        if (Math.abs(edge.dirVec.x) > Math.abs(edge.dirVec.y)) {
            // overwrite these functions if edge is horizontal
            dirXy = (obj: Vec2 | SimplePoint) => roundToArrowLengthGranularity(obj.x);
        }

        //Finds the direction of the end or beginning of the skeleton and figures out whether it is going towards the test edge
        if (beginningOfSkel) {
            if (
                !MathFunctions.isSameSign(
                    dirXy(shape.skeleton[1].pos) - dirXy(shape.skeleton[0].pos),
                    dirXy(shape.skeleton[0].pos) - dirXy(shape.cornerMap[testEdge.startCornerId].coords)
                )
            ) {
                continue;
            }
        } else if (
            !MathFunctions.isSameSign(
                dirXy(shape.skeleton[shape.skeleton.length - 2].pos) -
                    dirXy(shape.skeleton[shape.skeleton.length - 1].pos),
                dirXy(shape.skeleton[shape.skeleton.length - 1].pos) -
                    dirXy(shape.cornerMap[testEdge.startCornerId].coords)
            )
        ) {
            continue;
        }

        //if horizontal edge is on a diffent y than the keepout zone, there exist no possible collisions.
        if (Math.abs(edge.dirVec.x) > Math.abs(edge.dirVec.y)) {
            if (startCord.y < keepOutRect.tr.y || startCord.y > keepOutRect.bl.y) {
                continue;
            }
        } else if (startCord.x < keepOutRect.bl.x || startCord.x > keepOutRect.tr.x) {
            continue;
        }

        //Find the distance from both edges of the keepout rectangle from both the start and end coordinates
        const distanceFromStartCord = Math.min(
            Math.abs(dirXy(startCord) - dirXy(keepOutRect.tr)),
            Math.abs(dirXy(startCord) - dirXy(keepOutRect.bl))
        );
        const distanceFromEndCord = Math.min(
            Math.abs(dirXy(endCord) - dirXy(keepOutRect.tr)),
            Math.abs(dirXy(endCord) - dirXy(keepOutRect.bl))
        );

        const dist = Math.max(distanceFromEndCord, distanceFromStartCord);

        if (clearance) {
            clearance = Math.min(dist, clearance);
        } else if (dist) {
            clearance = dist;
        }
    }
    return clearance;
};

const buildKeepOutRectangle = (edge: DrawingEdge, shape: DrawingShape) => {
    const rect = { tr: new Vec2(0, 0), bl: new Vec2(0, 0) };
    const startCord = shape.cornerMap[edge.startCornerId].coords;
    const endCord = shape.cornerMap[edge.endCornerId].coords;

    if (Math.abs(edge.dirVec.x) > Math.abs(edge.dirVec.y)) {
        //edge is horizontal
        if (edge.dirVec.x > 0) {
            rect.tr = new Vec2(endCord.x + MINIMUM_GAP, endCord.y - MINIMUM_GAP);
            rect.bl = new Vec2(startCord.x - MINIMUM_GAP, startCord.y + MINIMUM_GAP);
        } else {
            rect.tr = new Vec2(startCord.x + MINIMUM_GAP, startCord.y - MINIMUM_GAP);
            rect.bl = new Vec2(endCord.x - MINIMUM_GAP, endCord.y + MINIMUM_GAP);
        }
    } else if (edge.dirVec.y > 0) {
        rect.tr = new Vec2(startCord.x + MINIMUM_GAP, startCord.y - MINIMUM_GAP);
        rect.bl = new Vec2(endCord.x - MINIMUM_GAP, endCord.y + MINIMUM_GAP);
    } else {
        rect.tr = new Vec2(endCord.x + MINIMUM_GAP, endCord.y - MINIMUM_GAP);
        rect.bl = new Vec2(startCord.x - MINIMUM_GAP, startCord.y + MINIMUM_GAP);
    }

    return rect;
};

//finds the opposing edge of the shape from the same skeleton
const findParallelEdge = (shape: DrawingShape, edge: DrawingEdge): DrawingEdge | undefined => {
    const skStartIndex = shape.cornerMap[edge.startCornerId].skIndex;
    const skEndIndex = shape.cornerMap[edge.endCornerId].skIndex;

    return shape.drawingEdges.find((testEdge) => {
        return (
            testEdge !== edge &&
            shape.cornerMap[testEdge.startCornerId].skIndex === skEndIndex &&
            shape.cornerMap[testEdge.endCornerId].skIndex === skStartIndex
        );
    });
};

interface enforceEdgeConstraintsParams {
    arrow: ArrowInfo;
    value: number;
    edgeType?: SideItemOption;
    shape: DrawingShape;
}

const getAdjustedEdgeValueByEdgeType = ({
    arrow,
    value = 0,
    edgeType,
    shape,
}: enforceEdgeConstraintsParams): number => {
    if (edgeType && !arrow.label) {
        switch (edgeType) {
            case SideItemOption.BACKSPLASH:
                return adjustValueForMinMax(value, BACKSPLASH_INPUT_LIMIT.MIN, BACKSPLASH_INPUT_LIMIT.MAX);
            case SideItemOption.WATERFALL:
                return adjustValueForMinMax(value, WATERFALL_INPUT_LIMIT.MIN, WATERFALL_INPUT_LIMIT.MAX);
        }
    }

    const edge = shape.drawingEdges.find((edge) => edge.id === arrow.drawingEdgeId);
    const adjacentSideWidths = getAdjacentSideWidths(shape, edge);
    const diagonalAdjustment = getDiagonalAdjustmentsForEdge(shape, edge);

    return adjustValueForMinMax(
        value,
        arrow.isRadiusOut || arrow.isBumpOut || arrow.isDiagonalCorner ? 0 : EDGE_LENGTH_INPUT_LIMIT.MIN,
        EDGE_LENGTH_INPUT_LIMIT.MAX - (adjacentSideWidths + diagonalAdjustment)
    );
};

const applyArrowMinMaxConstraints = ({ arrow, value }: enforceEdgeConstraintsParams): number => {
    let furtherRefinedValue = value;
    if (arrow.minimumLength) {
        furtherRefinedValue = Math.max(value, arrow.minimumLength);
    }
    if (arrow.maximumLength) {
        furtherRefinedValue = Math.min(furtherRefinedValue, arrow.maximumLength);
    }
    return furtherRefinedValue;
};

/**
 * Returns the sum of the adjustments to the maximum length for the given edge due to diagonal corners
 * @param shape the shape we're dealing with (to get the cornerMap)
 * @param edge the edge in question
 */
export const getDiagonalAdjustmentsForEdge = (shape: DrawingShape, edge?: DrawingEdge): number => {
    if (!edge) {
        return 0;
    }
    const corners = Object.values(shape.cornerMap);
    return [edge.startCornerId, edge.endCornerId].reduce((acc, id) => {
        const corner = corners.find((c) => c.id === id);
        const cornerAdjust = corner?.diagonalLength ? corner.diagonalLength / Math.sqrt(2) : 0;
        return acc + cornerAdjust;
    }, 0);
};

/**
 * Returns the sum of the adjustments to the maximum length for the given edge due to adjacent side widths
 * @param shape the shape we're dealing with (to get the cornerMap)
 * @param edge the edge in question
 */
export const getAdjacentSideWidths = (shape: DrawingShape, edge?: DrawingEdge): number => {
    if (!edge) {
        return 0;
    }
    const corners = Object.values(shape.cornerMap)
        .filter((corner) => [edge.startCornerId, edge.endCornerId].includes(corner.id))
        .sort((a, b) => a.skIndex - b.skIndex);

    return corners.reduce((acc, corner, currentIndex) => {
        if (!corner.angle || corner.angle >= 0) {
            return acc;
        }
        const adjacentSkindex = currentIndex === 0 ? corner.skIndex - 1 : corner.skIndex; // adjust for the lower index bone
        return acc + shape.skeleton[adjacentSkindex].depth * 2;
    }, 0);
};

/**
 * enforce constraints on edges, taking into account diagonal corners and inside corners
 * @param arrow
 * @param value
 * @param edgeType
 * @param shape
 */
export const enforceEdgeConstraints = ({ arrow, value = 0, edgeType, shape }: enforceEdgeConstraintsParams): number => {
    const adjustedValue = getAdjustedEdgeValueByEdgeType({ arrow, value, edgeType, shape });
    return roundToArrowLengthGranularity(applyArrowMinMaxConstraints({ arrow, value: adjustedValue, shape }));
};

export const enforceRadiusConstraints = (cornerInfo: CornerInfo, value = 0): number => {
    if (value < DEFAULT_CORNER_RADIUS) {
        return DEFAULT_CORNER_RADIUS;
    }

    if (!cornerInfo.maxAllowedRadius) {
        console.error('Tried to enforceRadiusConstraints without a maxAllowedRadius', cornerInfo);
    }

    if (cornerInfo.maxAllowedRadius) {
        const maxRadius = value <= cornerInfo.maxAllowedRadius ? value : cornerInfo.maxAllowedRadius;

        if (maxRadius < DEFAULT_CORNER_RADIUS) {
            return DEFAULT_CORNER_RADIUS;
        }

        //round the adjusted value to the nearest quarter inch
        return roundToCornerRadiusGranularity(maxRadius);
    }

    return 0;
};

export const calcMaxAllowedRadiusForAll = (shape: DrawingShape) => {
    Object.values(shape.cornerMap).forEach((corner) => calcMaxAllowedRadius(shape, corner));
};

// populates max allowed radius for a corner
const calcMaxAllowedRadius = (shape: DrawingShape, corner: CornerInfo): void => {
    // this needs lots of work for radius out and bump outs
    const incidentEdges = shape.drawingEdges.filter(
        (edge) => edge.startCornerId === corner.id || edge.endCornerId === corner.id
    );
    let maxAllowedRadius = Infinity;
    incidentEdges.forEach((edge) => {
        let oppositeCorner: CornerInfo | undefined;
        if (edge.startCornerId === corner.id) {
            oppositeCorner = shape.cornerMap[edge.endCornerId];
        } else {
            oppositeCorner = shape.cornerMap[edge.startCornerId];
        }
        if ((containsBumpOut(edge) || containsRadiusOut(edge)) && corner.size) {
            maxAllowedRadius = Math.min(maxAllowedRadius, corner.size);
        } else if (oppositeCorner.size) {
            maxAllowedRadius = Math.min(
                maxAllowedRadius,
                edge.dirVec.length() - maxAvailableForCornerOptions(oppositeCorner)
            );
        } else {
            maxAllowedRadius = Math.min(maxAllowedRadius, edge.dirVec.length() - DEFAULT_CORNER_RADIUS);
        }
    });

    corner.maxAllowedRadius = maxAllowedRadius;
};

// Function do determine if a given corner can handle a radius.
export function canApplyRadius(corner: CornerInfo, _shape: DrawingShape, radius: number): boolean {
    if (!corner.maxAllowedRadius) {
        return true;
    } else {
        return corner.maxAllowedRadius >= maxAvailableForCornerOptions(corner, radius);
    }
}

/**
 * find the maximum available size of either a radius or diagonal corner
 * @param corner
 * @param radius
 */
const maxAvailableForCornerOptions = (corner: CornerInfo, radius = 0): number => {
    let ret = 0;
    if (!corner.angle) {
        return 0;
    }
    if (radius !== 0) {
        // the radius argument is used when validating a corner can take a particular radius value
        ret = Math.abs(radius / Math.tan(MathFunctions.convertToRadians(corner.angle / 2)));
        return ret;
    }
    // if no radius argument is passed, the intent is in calculating preset constriants on radii
    if (corner.size) {
        ret = roundToCornerRadiusGranularity(
            Math.abs(corner.size / Math.tan(MathFunctions.convertToRadians(corner.angle / 2)))
        );
    }
    if (corner.diagonalLength) {
        ret =
            corner.angle! % 135
                ? Math.sqrt(Math.pow(corner.diagonalLength, 2) / 2)
                : corner.diagonalLength * (MAGIC_NUMBER_2 / 12);
    }
    return ret;
};
